import { ClassConstructor, plainToClassFromExist } from 'class-transformer';
import { BehaviorSubject, merge, of } from 'rxjs';
import { concatMap, tap, map, catchError, toArray } from 'rxjs/operators';
import { FindAllQueryDto, FindAllQueryResponseDto } from '@models';
import { TypeUtils } from '@utils/type';
import { RxUtils } from '@utils/rx';
import { HttpRequestGenericOptions, httpService } from '@services/http';
import { HttpServiceBase } from './HttpServiceBase';

type IdType = string | number;

export interface CRUDServiceConfig<Entity> {
	primaryKey?: keyof Entity;
	searchPath?: string;
	createPath?: string;
	getAllPath?: string;
}

export abstract class CrudServiceBase<Entity extends { id: IdType }> extends HttpServiceBase {
	protected cache$: BehaviorSubject<Entity[]>;

	constructor(private entity: ClassConstructor<Entity>, path: string, private cfg?: CRUDServiceConfig<Entity>) {
		super(path);
	}

	getAllFromCache() {
		this.cache$ = this.cache$ || new BehaviorSubject<Entity[]>([]);
		if (!this.cache$.getValue().length) {
			return this.getAll().pipe(
				catchError(() => of([])),
				concatMap((entities) => (this.cache$.next(entities), this.cache$))
			);
		}
		return this.cache$;
	}

	protected convert<TData>(data: TData): TData {
		return TypeUtils.convert(this.entity, data) as unknown as TData;
	}

	protected convertToFindAllQueryResponseDto(data: FindAllQueryResponseDto<Entity>) {
		return plainToClassFromExist(new FindAllQueryResponseDto<Entity>(this.entity), data);
	}

	get(id: IdType) {
		return httpService.get<Entity>(this.getUrl(), `${id}`).pipe(
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((entity) => this.convert(entity)),
			tap((result) => this.logger.debug(`[${this.constructor.name}.${this.get.name}]`, result))
		);
	}

	getAll() {
		return httpService.get<Entity[]>(this.getUrl(), `${this.cfg?.getAllPath || ''}`).pipe(
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((entities) => TypeUtils.convert(this.entity, entities)),
			tap((result) => this.logger.debug(`[${this.constructor.name}.${this.getAll.name}]`, result)),
			map((entities) => (this.cache$?.next(entities), entities))
		);
	}

	getAllPaginated(query?: FindAllQueryDto<Entity>, opts?: Omit<HttpRequestGenericOptions, 'params'>) {
		return httpService
			.get<FindAllQueryResponseDto<Entity>>(this.getUrl(), `${this.cfg?.getAllPath || ''}`, { ...opts, params: query })
			.pipe(
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
				map((entities) => this.convertToFindAllQueryResponseDto(entities)),
				tap((result) => this.logger.debug(`[${this.constructor.name}.${this.getAllPaginated.name}]`, result))
			);
	}

	search(text: string) {
		return httpService.get<Entity[]>(this.getUrl(), `${this.cfg?.searchPath || 'search'}/${text}`).pipe(
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((entities) => TypeUtils.convert(this.entity, entities)),
			tap((result) => this.logger.debug(`[${this.constructor.name}.${this.search.name}]`, result))
		);
	}

	create(entity: Partial<Entity>) {
		return httpService
			.post<Entity>(this.getUrl(), `${this.cfg?.createPath || ''}`, { body: entity, errorHandler: 'notification' })
			.pipe(
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
				map((result) => this.convert(result)),
				tap((result) => this.logger.debug(`[${this.constructor.name}.${this.create.name}]`, result))
			);
	}

	update(id: IdType, entity: Partial<Entity>) {
		return httpService.patch<Entity>(this.getUrl(), `${id}`, { body: entity, errorHandler: 'notification' }).pipe(
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => this.convert(result)),
			tap((result) => this.logger.debug(`[${this.constructor.name}.${this.update.name}]`, result))
		);
	}

	delete(entity: Entity) {
		return httpService
			.delete<unknown>(this.getUrl(), `${entity[(this.cfg?.primaryKey || 'id') as keyof Entity]}`, { errorHandler: 'notification' })
			.pipe(
				tap((result) => this.logger.debug(`[${this.constructor.name}.${this.delete.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
			);
	}

	deleteMultiple(entities: Entity[]) {
		return merge(...entities.map((v) => this.delete(v))).pipe(
			tap((result) => this.logger.debug(`[${this.constructor.name}.${this.deleteMultiple.name}]`, result)),
			catchError((e) => {
				this.logger.error(e);
				return of(null);
			}),
			toArray()
		);
	}
}
